import type { Formatter, Series } from './types';

interface ChartTableProps {
  id: string;
  title: string;
  categoryLabel: string;
  series: readonly Series[];
  format: Formatter;
}

/**
 * The chart's numbers, as a real table, for screen readers.
 *
 * `role="img"` plus an `aria-label` is the right answer for a sparkline and the
 * wrong one for a four-series, sixty-point chart: that label is two hundred and
 * forty numbers in a single uninterruptible string. It passes an audit and
 * helps nobody.
 *
 * A `<table>` is navigable. Row and column commands work, headers are announced
 * with each cell, and a reader can jump to the number they wanted instead of
 * listening to the other two hundred and thirty-nine.
 *
 * ## Why `sr-only` and not a `<details>` disclosure
 *
 * Offering it to everyone sounds more generous and is worse. Sighted keyboard
 * users would then tab through a hidden table on every chart on the page — and
 * the numbers are already available to them twice over, in the section's own
 * table and in its Excel export. This is the route for people who cannot use
 * the drawing; it is not a second copy of the page.
 */
export function ChartTable({ id, title, categoryLabel, series, format }: ChartTableProps) {
  // Categories come from the union of every series, in first-seen order, so a
  // series with a gap does not shorten the table.
  const keys: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const one of series) {
    for (const point of one.points) {
      if (seen.has(point.key)) continue;
      seen.add(point.key);
      keys.push({ key: point.key, label: point.label });
    }
  }

  const valueAt = (one: Series, key: string) => {
    const point = one.points.find((candidate) => candidate.key === key);
    // "No data" spelled out, because an empty cell is read as silence and is
    // indistinguishable from a cell the reader skipped past.
    return point?.value == null ? 'No data' : format(point.value);
  };

  return (
    <table id={id} className="sr-only">
      <caption>{title} — data table</caption>
      <thead>
        <tr>
          <th scope="col">{categoryLabel}</th>
          {series.map((one) => (
            <th key={one.id} scope="col">
              {one.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {keys.map(({ key, label }) => (
          <tr key={key}>
            <th scope="row">{label}</th>
            {series.map((one) => (
              <td key={one.id}>{valueAt(one, key)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
